import React from 'react';
import { ArrowsRightLeftIcon } from '@heroicons/react/24/outline';
import { HChapter, HMetaphor, HSection, HParagraph, HStep, HTip, HBold } from '../HandbookContent';

export default function ChapterTransitions() {
  return (
    <HChapter
      title="Transitions: Moving Between Statuses"
      subtitle="Transitions are the allowed paths between statuses — how a matter progresses through the workflow."
    >
      <HMetaphor
        icon={<ArrowsRightLeftIcon className="w-5 h-5 text-lex-accent" />}
        title="Think of Transitions as On-Ramps and Off-Ramps"
      >
        If statuses are towns on your highway map, transitions are the roads connecting them. Not every
        town connects to every other town — you can only take the roads that exist. A transition defines
        which status you can move to, what triggers the move, and what happens along the way.
      </HMetaphor>

      <HSection title="How Transitions Work">
        <img
          src="/handbook/transition-arrow.svg"
          alt="Two status nodes (Filed and Served, both In Progress) connected by a bezier transition arrow with a 'manual' trigger badge, transition name 'Complete Service', and counts showing 2 guards, 1 validator, 3 actions"
          className="rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm mb-4 w-full"
        />
        <HParagraph>
          Each transition has a <HBold>source status</HBold> (where you're coming from) and a
          <HBold> target status</HBold> (where you're going). When you execute a transition, the
          matter moves from the source to the target.
        </HParagraph>
        <HParagraph>
          A transition can only happen when all of its <HBold>guards</HBold> are satisfied (conditions
          that must be true) and all <HBold>validators</HBold> pass (data checks). More on those in the
          next chapter.
        </HParagraph>
      </HSection>

      <HSection title="Trigger Types">
        <HParagraph>
          Not all transitions require someone to click a button. EdgePlaybook supports four trigger types:
        </HParagraph>
        <ul className="list-disc pl-6 text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-4 space-y-2">
          <li>
            <HBold>Manual</HBold> — A person clicks a button to move the matter forward. This is the most
            common type. Example: an attorney clicks "Mark as Served" after filing proof of service.
          </li>
          <li>
            <HBold>Event</HBold> — The system triggers the transition when something happens elsewhere.
            Example: when a document is uploaded to the case, it automatically moves from "Awaiting
            Documents" to "Under Review."
          </li>
          <li>
            <HBold>Deadline</HBold> — A court rule or calendar deadline triggers the transition.
            Example: 30 days after filing, the case automatically moves to the discovery phase.
          </li>
          <li>
            <HBold>Automatic</HBold> — The transition fires immediately when the source status is reached.
            Useful for intermediate processing steps that don't require human input.
          </li>
        </ul>
      </HSection>

      <HSection title="Creating a Transition in the Visual Editor">
        <HStep number={1} title="Enter connection mode">
          Click the "Transition" button in the editor toolbar (the arrow icon). This activates
          draw-edge mode.
        </HStep>
        <HStep number={2} title="Click the source status">
          Click on the status node where the transition starts. You'll see a connection handle appear.
        </HStep>
        <HStep number={3} title="Drag to the target status">
          Drag from the source to the target status node. A new transition arrow appears connecting them.
        </HStep>
        <HStep number={4} title="Configure the transition">
          Click the new transition arrow to open the property panel on the right. Here you can set the
          transition name, trigger type, guards, validators, and actions.
        </HStep>
      </HSection>

      <HTip>
        You can create transitions that loop back to the same status. This is useful for "retry" scenarios —
        for example, a "Rejected" transition that sends the matter back to "Under Review" for corrections.
      </HTip>
    </HChapter>
  );
}
